import { Box, Divider, Stack, Typography, IconButton } from "@mui/material";
import {
	DeviceState,
	DeviceStatus,
} from "Infrastructure/Models/Messages/Dto/DeviceStatus";
import { DeviceForm } from "Infrastructure/Utils/Nsure/DeviceUtils";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import { HubConnection } from "@microsoft/signalr";
import { CloseDialogHandler } from "Infrastructure/Providers/DialogProvider/DialogProvider";
import DeviceFormComponent from "../DeviceFormComponent";
import { ProductEnableDisableMessage } from "Infrastructure/Models/Messages/Messages";
import ProductListTable from "./ProductListTable";
import { ProductStatusDeviceModel } from "Infrastructure/Utils/Nsure/ProductUtils";
import { ActionHistorySelect } from "../ActionHistorySelect";
import { ActionHistoryDto } from "Infrastructure/Api/Api";
import { ActionType } from "Infrastructure/Api/ApiCustom";
import ClearIcon from "@mui/icons-material/Clear";
import { v4 as uuidv4 } from "uuid";
import { useSnackbar } from "Infrastructure/Providers/SnackbarProvider/SnackbarProvider";

interface ProductStatusSelectionModel {
	categories: number[];
	productTypes: number[];
	products: number[];
	tariffs: number[];
}

type Props = {
	deviceStatuses: DeviceStatus[] | null;
	deviceStatus?: DeviceStatus | undefined;
	productDeviceModel: ProductStatusDeviceModel | null;
	hubConnection: HubConnection | null;
	closeDialog: CloseDialogHandler;
	onEnableDisable: (message: ProductEnableDisableMessage) => void;
};

const ProductActions: FunctionComponent<Props> = (props) => {
	const {
		deviceStatuses,
		deviceStatus,
		productDeviceModel,
		hubConnection,
		closeDialog,
		onEnableDisable,
	} = props;
	const showSnackbar = useSnackbar();

	if (!deviceStatuses || deviceStatuses?.length === 0) {
		return (
			<Typography variant="caption">Žádné instance k dispozici</Typography>
		);
	}

	const [form, setForm] = useState<DeviceForm>({
		devices: !!deviceStatus
			? [
					{
						name: deviceStatus.deviceName,
						isChecked: true,
						isDisabled: false,
						deviceStatus,
					},
			  ]
			: deviceStatuses.map((x) => ({
					name: x.deviceName,
					isChecked: false,
					isDisabled:
						x.deviceState !== DeviceState.Connected || x.isSelfInsurance,
					deviceStatus: x,
			  })),
	});

	// Wrap setForm to clear isHistoryLoaded on any change
	const setFormWithHistoryCheck = (
		updater: React.SetStateAction<DeviceForm>
	) => {
		setForm((prev: DeviceForm) => {
			const next = typeof updater === "function" ? updater(prev) : updater;
			if (isHistoryLoaded && !justLoadedHistory.current) {
				setIsHistoryLoaded(false);
				setSelectedHistory(null);
			}
			return next;
		});
	};

	const [selectionModel, setSelectionModel] = useState<ProductStatusSelectionModel>({
		categories: [],
		productTypes: [],
		products: [],
		tariffs: [],
	});

	const [productEnableDisableMessage, setProductEnableDisableMessage] = useState<ProductEnableDisableMessage>({
		requestId: null,
		filteredDeviceNames: [],
		categories: [],
		productTypes: [],
		products: [],
		tariffs: [],
		reason: null,
	});

	const [selectedHistory, setSelectedHistory] = useState<ActionHistoryDto | null>(null);
	const [isHistoryLoaded, setIsHistoryLoaded] = useState(false);
	const justLoadedHistory = useRef(false);

	// Mapping function to convert parsed JSON to ProductEnableDisableMessage
	function mapParsedJsonToProductEnableDisableMessage(parsed: any): ProductEnableDisableMessage {
		return {
			requestId: uuidv4(),
			filteredDeviceNames: parsed.FilteredDeviceNames || parsed.filteredDeviceNames || [],
			categories: parsed.Categories?.map((c: any) => ({
				id: c.ID ?? c.Id ?? c.id,
				doEnable: c.DoEnable ?? c.doEnable ?? false
			})) || [],
			productTypes: parsed.ProductTypes?.map((pt: any) => ({
				id: pt.ID ?? pt.Id ?? pt.id,
				doEnable: pt.DoEnable ?? pt.doEnable ?? false
			})) || [],
			products: parsed.Products?.map((p: any) => ({
				id: p.ID ?? p.Id ?? p.id,
				doEnable: p.DoEnable ?? p.doEnable ?? false,
				doEnableContractCreate: p.DoEnableContractCreate ?? p.doEnableContractCreate ?? null
			})) || [],
			tariffs: parsed.Tariffs?.map((t: any) => ({
				id: t.ID ?? t.Id ?? t.id,
				doEnable: t.DoEnable ?? t.doEnable ?? false
			})) || [],
			reason: null
		};
	}

	// Utility: Map filteredDeviceNames to deviceForm
	function updateDeviceFormWithFilteredNames(
		deviceForm: DeviceForm,
		filteredDeviceNames: string[] = []
	) {
		const unavailableDevices: string[] = [];
		
		const updatedDevices = deviceForm.devices.map((device) => {
			const shouldBeChecked = filteredDeviceNames.includes(device.name);
			
			// If device should be checked but is disabled, add to unavailable list
			if (shouldBeChecked && device.isDisabled) {
				unavailableDevices.push(device.name);
			}
			
			return {
				...device,
				isChecked: shouldBeChecked && !device.isDisabled, // Only check if not disabled
			};
		});
		
		// Show snackbar if there are unavailable devices
		if (unavailableDevices.length > 0) {
			showSnackbar(
				`Nepodařilo se načíst některé instance: ${unavailableDevices.join(', ')}`,
				"error"
			);
		}
		
		return {
			...deviceForm,
			devices: updatedDevices,
		};
	}

	// Effect: clear history selection on message change
	// biome-ignore lint/correctness/useExhaustiveDependencies: form, productEnableDisableMessage are intentional change triggers
	useEffect(() => {
		if (isHistoryLoaded) {
			if (justLoadedHistory.current) {
				justLoadedHistory.current = false;
			} else {
				// Clear history when any form data changes manually
				setIsHistoryLoaded(false);
				setSelectedHistory(null);
			}
		}
	}, [form, productEnableDisableMessage, isHistoryLoaded]);

	const clearAllData = () => {
		setForm((prev) => ({
			...prev,
			devices: prev.devices.map((d) => ({ ...d, isChecked: false })),
		}));
		setSelectionModel({
			categories: [],
			productTypes: [],
			products: [],
			tariffs: [],
		});
		setSelectedHistory(null);
		setIsHistoryLoaded(false);
	};

	return (
		<Stack>
			<Box>
				<Stack
					direction="row"
					spacing={1}
					alignItems="center"
					width="900px"
					justifySelf="end"
				>
					<ActionHistorySelect
						device={deviceStatus?.deviceName}
						actionTypes={[ActionType.ProductEnableDisable]}
						value={selectedHistory}
						onSelect={(action) => {
							clearAllData();
							setSelectedHistory(action);
							if (action) {
								try {
									const parsed = JSON.parse(action.messageJson);
									const filteredDeviceNames = parsed.FilteredDeviceNames || parsed.filteredDeviceNames || [];
									setFormWithHistoryCheck((prev) => updateDeviceFormWithFilteredNames(prev, filteredDeviceNames));
									if (action.actionType === ActionType.ProductEnableDisable) {
										setProductEnableDisableMessage(mapParsedJsonToProductEnableDisableMessage(parsed));
									}
									setIsHistoryLoaded(true);
									justLoadedHistory.current = true;
								} catch {
									// showSnackbar("Nepodařilo se načíst historickou akci.", "error");
								}
							}
						}}
						disabled={isHistoryLoaded}
					/>
					<IconButton
						size="small"
						onClick={clearAllData}
						disabled={!isHistoryLoaded}
					>
						<ClearIcon sx={{ fontSize: "1.2rem" }} />
					</IconButton>
				</Stack>
			</Box>
			{!deviceStatus && (
				<Box mb={1}>
					<Typography variant="h6" component="div">
						Instance
					</Typography>
					<Divider />
					<Box ml={1.5} mt={1}>
						<DeviceFormComponent
							deviceStatuses={deviceStatuses}
							deviceStatus={deviceStatus}
							form={form}
							setForm={setFormWithHistoryCheck}
						/>
					</Box>
				</Box>
			)}
			<Stack>
				<ProductListTable
					deviceForm={form}
					productDeviceModel={productDeviceModel}
					hubConnection={hubConnection}
					closeDialog={closeDialog}
					onEnableDisable={onEnableDisable}
					selectionModel={selectionModel}
					setSelectionModel={setSelectionModel}
					productEnableDisableMessage={productEnableDisableMessage}
					setProductEnableDisableMessage={setProductEnableDisableMessage}
					isHistoryLoaded={isHistoryLoaded}
				/>
			</Stack>
		</Stack>
	);
};

export default ProductActions;
